import { availableMapEvents, getMapEvent, getStageRewardPools, lookupCard } from "../../content/index.js";
import { createCardInstance } from "../deck/createInstance.js";
import { advanceLcg } from "../rng/lcg.js";
import type {
  ActiveMapEvent,
  CardId,
  EventId,
  MapEventOfferEffect,
  NextCaseModifiers,
  NodeId,
  RunState,
} from "../types/index.js";

export type ResolveMapEventResult = {
  run: RunState;
  activeEvent: ActiveMapEvent;
};

const EMPTY_MODIFIERS: NextCaseModifiers = { deadline: 0, required: 0, energy: 0 };

function addCard(run: RunState, cardId: CardId): RunState {
  const created = createCardInstance({ nextCardSequence: run.nextCardSequence }, cardId);
  return {
    ...run,
    discard: [...run.discard, created.instance],
    nextCardSequence: created.nextCardSequence,
  };
}

function randomItem<T>(items: readonly T[], rngState: number): { item: T; rngState: number } {
  if (items.length === 0) throw new Error("randomItem: items must not be empty");
  const next = advanceLcg(rngState);
  return {
    item: items[Math.floor(next.value * items.length)]!,
    rngState: next.state,
  };
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : `${value}`;
}

export function resolveMapEvent(
  run: RunState,
  nodeId: NodeId,
  forcedEventId?: EventId,
): ResolveMapEventResult {
  let rngState = run.rngState;
  let eventId = forcedEventId;

  if (!eventId) {
    const picked = randomItem(availableMapEvents(run), rngState);
    eventId = picked.item.id;
    rngState = picked.rngState;
  }

  const event = getMapEvent(eventId);
  let nextRun: RunState = { ...run, rngState };
  const effects: MapEventOfferEffect[] = [];

  for (const effect of event.effects) {
    if (effect.kind === "addRandomAction") {
      const picked = randomItem(getStageRewardPools(nextRun.stageId)[effect.tier].actions, nextRun.rngState);
      nextRun = { ...nextRun, rngState: picked.rngState };
      effects.push({ kind: "addCard", cardId: picked.item });
    } else {
      effects.push(effect);
    }
  }

  return {
    run: nextRun,
    activeEvent: {
      nodeId,
      eventId,
      effects,
      effectText: effects.map(formatEffect).join("\n"),
    },
  };
}

function formatEffect(effect: MapEventOfferEffect): string {
  switch (effect.kind) {
    case "fatigue":
      return `疲労 ${signed(effect.amount)}`;
    case "addCard":
      return `「${lookupCard(effect.cardId)?.name ?? effect.cardId}」を獲得`;
    case "upgradeCard":
      return `「${lookupCard(effect.from)?.name ?? effect.from}」1枚を「${lookupCard(effect.to)?.name ?? effect.to}」に育成`;
    case "nextCase": {
      const labels = { deadline: "次のプロジェクトの工期", required: "次のプロジェクトの必要進捗", energy: "次のプロジェクトの開始時の行動" };
      return `${labels[effect.stat]} ${signed(effect.amount)}`;
    }
  }
}

export function applyMapEventOffer(run: RunState, effects: readonly MapEventOfferEffect[]): RunState {
  let nextRun = run;
  for (const effect of effects) {
    switch (effect.kind) {
      case "fatigue":
        nextRun = { ...nextRun, fatigue: Math.max(0, nextRun.fatigue + effect.amount) };
        break;
      case "addCard":
        nextRun = addCard(nextRun, effect.cardId);
        break;
      case "upgradeCard": {
        let upgraded = false;
        const upgrade = (cards: RunState["deck"]): RunState["deck"] => cards.map((card) => {
          if (!upgraded && card.cardId === effect.from) {
            upgraded = true;
            return { ...card, cardId: effect.to };
          }
          return card;
        });
        nextRun = {
          ...nextRun,
          deck: upgrade(nextRun.deck),
          discard: upgrade(nextRun.discard),
          hand: upgrade(nextRun.hand),
          inPlay: upgrade(nextRun.inPlay),
          aside: upgrade(nextRun.aside),
        };
        break;
      }
      case "nextCase": {
        const modifiers = nextRun.nextCaseModifiers ?? EMPTY_MODIFIERS;
        nextRun = {
          ...nextRun,
          nextCaseModifiers: {
            ...modifiers,
            [effect.stat]: modifiers[effect.stat] + effect.amount,
          },
        };
        break;
      }
    }
  }
  return nextRun;
}
